import { Badge, Box, IconButton, Paper } from "@mui/material";
import { useChatRoom } from "chat/hooks/useChatRoom";
import { useGameRoom } from "context/GameContext";
import { useRoomTemplateLoginPageContext } from "context/RoomTemplateContext";
import { useWindowTilesContext } from "desktopWindow/context/WindowTilesContext";
import { getUnreadMails } from "email/emailHelpers";
import { useMails } from "email/hooks/useMails";
import { motion } from "framer-motion";
import { GameTutorialElements } from "gameTutorial/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import FileBrowserIcon from "../assets/img/game-room/finder.png";
import MailIcon from "../assets/img/game-room/mail.png";
import NoteIcon from "../assets/img/game-room/note.png";
import { ReactComponent as BrowserIcon } from "assets/svg/new-safari.svg";
import ChatIcon from "../assets/img/game-room/whatsapp.png";
import TrashIcon from "../assets/img/game-room/trash.png";
import { isDesktop } from "react-device-detect";
import { autoSetViewBox } from "utils/helpers/svg";

const TaskbarMacOS = () => {
	const intl = useIntl();
	const { value: roomTemplate } = useRoomTemplateLoginPageContext();
	const { email = true, notes, fileBrowser = true } = roomTemplate?.featureConfig || {};
	const { roomId } = useGameRoom();
	const { mails } = useMails(roomId);
	const { unreadMessages } = useChatRoom(roomId, null);
	const { isWindowOpened, openWindow } = useWindowTilesContext();

	const [hoveredIndex, setHoveredIndex] = useState(null);

	const apps = [
		fileBrowser
			? {
					testId: "filebrowser",
					className: GameTutorialElements.FILE_BUTTON,
					iconUrl: FileBrowserIcon,
					onClick: () =>
						openWindow({
							id: "filebrowser",
							componentName: "FileBrowser",
							windowTitle: intl.formatMessage({ id: "FILE_BROWSER.window.title" }),
							size: {
								height: 480,
								width: 776,
							},
							minSize: {
								height: 480,
								width: 776,
							},
						}),
			  }
			: null,
		email
			? {
					testId: "email",
					className: GameTutorialElements.EMAIL_BUTTON,
					iconUrl: MailIcon,
					badgeContent: getUnreadMails(mails).length,
					onClick: () =>
						openWindow({
							id: "email",
							componentName: "Email",
							windowTitle: intl.formatMessage({ id: "EMAIL.window.title" }),
							size: {
								height: 560,
								width: 1175,
							},
							minSize: {
								width: 990,
								height: 500,
							},
							verticalScroll: false,
						}),
			  }
			: null,
		{
			testId: "chat",
			className: GameTutorialElements.CHAT_BUTTON,
			iconUrl: ChatIcon,
			badgeContent: unreadMessages.length,
			onClick: () =>
				openWindow({
					id: "chat",
					componentName: "Chat",
					windowTitle: intl.formatMessage({ id: "CHAT.window.title" }),
					size: {
						height: 464,
						width: 512,
					},
					minSize: {
						height: 480,
						width: 512,
					},
				}),
		},
		notes
			? {
					testId: "notes",
					className: GameTutorialElements.NOTE_BUTTON,
					iconUrl: NoteIcon,
					onClick: () =>
						openWindow({
							id: "notes",
							componentName: "Notes",
							windowTitle: intl.formatMessage({ id: "NOTES.window.title" }),
							size: {
								height: 480,
								width: 800,
							},
						}),
			  }
			: null,
		isDesktop && {
			testId: "BrowserBtn",
			className: GameTutorialElements.BROWSER_BUTTON,
			iconUrl: BrowserIcon,
			onClick: () => window.open("https://google.com", "_blank"),
		},
	].filter(Boolean);

	// Function to dynamically calculate scaling
	const getScale = useCallback(
		(index) => {
			if (hoveredIndex === null) return 1;
			const distance = Math.abs(hoveredIndex - index);
			return distance === 0 ? 1.3 : distance === 1 ? 1.2 : 1;
		},
		[hoveredIndex],
	);

	// Function to calculate the vertical translation for hover effect
	const getTranslateY = (index: number) => {
		if (hoveredIndex === null) return 0;
		const distance = Math.abs(hoveredIndex - index);
		return distance === 0 ? -15 : distance === 1 ? -10 : 0;
	};

	// Function to calculate width change of the bar
	const getBarWidth = () => {
		const baseWidth = isDesktop ? 400 : 350; // Base width of the bar in pixels
		if (hoveredIndex === null) return baseWidth; // Default width when no hover
		return baseWidth + 20; // Increase width by 50 pixels on hover
	};

	const handleMouseEnter = (index) => setHoveredIndex(index);
	const handleMouseLeave = () => setHoveredIndex(null);

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const timeout = setTimeout(() => {
			autoSetViewBox(svgRef.current);
		}, 50);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				position: "fixed",
				bottom: "20px", // Make it float a little above the bottom
				width: "100%",
				zIndex: isDesktop ? 999 : 1,
			}}
		>
			{/* Floating bar container */}
			<motion.div
				animate={{ width: getBarWidth() }} // Animate width change
				transition={{ type: "spring", stiffness: 200, damping: 20 }}
				style={{ display: "flex", justifyContent: "center" }}
			>
				<Paper
					elevation={4}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "10px 20px",
						borderRadius: "16px",
						backgroundColor: "#F6F6F62C",
						backdropFilter: "blur(10px)",
						transition: "width 0.3s ease-in-out", // Smooth transition for width change
						width: "100%",
						// border: "1px solid #1A1A1A75",
					}}
				>
					{apps.map((app, index) => (
						<motion.div
							key={index}
							// whileHover={{ scale: getScale(index), transform: "translateY(-100px)" }}
							animate={{
								scale: getScale(index),
								translateY: getTranslateY(index),
							}}
							onHoverStart={() => handleMouseEnter(index)}
							onHoverEnd={handleMouseLeave}
							transition={{ type: "spring", stiffness: 300, damping: 20 }}
							style={{
								margin: isDesktop ? "0 6px" : "0 10px",
								display: "inline-block",
							}}
						>
							<Badge
								id={"text-unread-" + app!.testId}
								badgeContent={app!.badgeContent || 0}
								showZero={false}
								color="error"
								sx={{
									"& .MuiBadge-badge": {
										right: 5,
										top: 5,
									},
								}}
							>
								<IconButton
									data-testId={app!.testId}
									className={app!.className}
									sx={{
										width: isDesktop ? 50 : 60,
										height: isDesktop ? 50 : 60,
										// transform: `scale(${getScale(index)})`,
										transition: "transform 0.2s ease-out",
										...(app!.testId === "BrowserBtn" && {
											"& svg": {
												transform: "scale(1.2)",
											},
										}),
									}}
									onClick={app!.onClick}
									disableFocusRipple
									disableTouchRipple
									disableRipple
								>
									{app!.testId === "BrowserBtn" ? (
										<BrowserIcon ref={svgRef} />
									) : (
										<img
											src={app!.iconUrl}
											alt="icon"
											style={{ width: isDesktop ? 40 : 50, height: isDesktop ? 40 : 50 }}
										/>
									)}
									{isWindowOpened(app!.testId) && (
										<Box
											sx={{
												position: "absolute",
												bottom: "-5px", // Adjust as needed
												left: "50%",
												transform: "translateX(-50%)",
												width: "4px",
												height: "4px",
												backgroundColor: "white",
												borderRadius: "50%",
											}}
										/>
									)}
								</IconButton>
							</Badge>
						</motion.div>
					))}

					{isDesktop && (
						<>
							{/* Vertical separator */}
							<div
								style={{
									width: 1,
									height: 40,
									backgroundColor: "#000000",
									opacity: 0.2,
									marginLeft: 6,
								}}
							>
								&nbsp;
							</div>
							<motion.div
								animate={{
									scale: getScale(apps.length - 1),
									translateY: getTranslateY(apps.length - 1),
								}}
								onHoverStart={() => handleMouseEnter(apps.length - 1)}
								onHoverEnd={handleMouseLeave}
								transition={{ type: "spring", stiffness: 300, damping: 20 }}
								style={{
									margin: "0 6px",
									display: "inline-block",
								}}
							>
								<IconButton
									sx={{
										width: 50,
										height: 50,
										// transform: `scale(${getScale(index)})`,
										transition: "transform 0.2s ease-out",
									}}
									disableFocusRipple
									disableTouchRipple
									disableRipple
									onClick={() => {
										openWindow({
											id: "trash",
											componentName: "FileBrowser",
											windowTitle: intl.formatMessage({ id: "FILE_BROWSER.window.title" }),
											size: {
												height: 480,
												width: 776,
											},
											minSize: {
												height: 480,
												width: 776,
											},
											componentProps: {
												rootFolderId: "Trash",
											},
										});
									}}
								>
									<img src={TrashIcon} alt="icon" style={{ width: 40, height: 40 }} />
									{isWindowOpened("trash") && (
										<Box
											sx={{
												position: "absolute",
												bottom: "-5px", // Adjust as needed
												left: "50%",
												transform: "translateX(-50%)",
												width: "4px",
												height: "4px",
												backgroundColor: "white",
												borderRadius: "50%",
											}}
										/>
									)}
								</IconButton>
							</motion.div>
						</>
					)}
				</Paper>
			</motion.div>
		</Box>
	);
};

export default TaskbarMacOS;
